/**
 * Rule Engine - Core evaluation logic for Asset Performance System
 *
 * CRITICAL: This engine is deterministic and data-driven
 * - No hard-coded entity names
 * - No asset-specific logic
 * - All behavior emerges from SharePoint data
 */

class RuleEngine {
  constructor() {
    this.evaluationCache = new Map();
  }

  /**
   * Main evaluation function
   * Returns: { results, explanation, metadata }
   */
  async evaluateAsset(assetId, entities, signals, rules) {
    console.log(`[RuleEngine] Evaluating asset: ${assetId}`);

    // Clear cache for new evaluation
    this.evaluationCache.clear();

    // Step 1: Get latest signal per entity
    const latestSignals = this.getLatestSignals(entities, signals);

    // Step 2: Sort rules by priority (lower = first)
    const sortedRules = this.sortRulesByPriority(rules);

    // Step 3: Evaluate each rule
    const ruleEvaluations = this.evaluateRules(
      sortedRules,
      latestSignals,
      entities,
    );

    // Step 4: Calculate BASE target metrics
    let targetMetrics = this.calculateTargetMetrics(entities, latestSignals);

    // Step 4.5: Adjust targets for met contributions (NEW)
    targetMetrics = this.adjustTargetsForMetContributions(
      targetMetrics,
      ruleEvaluations,
      entities,
    );

    // Step 5: Calculate achievable metrics (now uses ADJUSTED targets)
    const achievableMetrics = this.calculateAchievableMetrics(
      entities,
      latestSignals,
      ruleEvaluations,
      targetMetrics, // Pass adjusted targets
    );

    // Step 6: Identify blocked/unrealized upside
    const blockedAnalysis = this.calculateBlockedUpside(
      targetMetrics,
      achievableMetrics,
      ruleEvaluations,
    );

    // Step 7: Generate dependency graph
    const dependencyGraph = this.buildDependencyGraph(
      entities,
      rules,
      ruleEvaluations,
    );

    // Step 8: Generate explanations
    const explanations = this.generateExplanations(
      entities,
      latestSignals,
      ruleEvaluations,
      targetMetrics,
      achievableMetrics,
      blockedAnalysis,
    );

    // Step 9: Identify dependency alerts
    const dependencyAlerts = this.identifyDependencyAlerts(
      entities,
      ruleEvaluations,
      latestSignals,
    );

    return {
      targetMetrics,
      achievableMetrics,
      blockedAnalysis,
      dependencyGraph,
      explanations,
      ruleEvaluations,
      dependencyAlerts,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Get latest signal per entity
   */
  getLatestSignals(entities, signals) {
    const latestSignals = new Map();

    entities.forEach((entity) => {
      const entitySignals = signals
        .filter((s) => s.EntityIdLookupId === entity.Id)
        .sort((a, b) => new Date(b.Timestamp) - new Date(a.Timestamp));

      if (entitySignals.length > 0) {
        latestSignals.set(entity.Id, entitySignals[0]);
      }
    });

    return latestSignals;
  }

  /**
   * Sort rules by priority (ascending)
   */
  sortRulesByPriority(rules) {
    return [...rules]
      .filter((r) => r.IsActive)
      .sort((a, b) => (a.Priority || 10) - (b.Priority || 10));
  }

  /**
   * Evaluate all dependency rules
   */
  evaluateRules(rules, signals, entities) {
    const evaluations = [];

    rules.forEach((rule) => {
      const sourceSignal = signals.get(rule.SourceEntityIdLookupId);
      const targetEntity = entities.find(
        (e) => e.Id === rule.TargetEntityIdLookupId,
      );
      const sourceEntity = entities.find(
        (e) => e.Id === rule.SourceEntityIdLookupId,
      );

      if (!sourceSignal) {
        evaluations.push({
          ruleId: rule.Id,
          ruleTitle: rule.Title,
          ruleType: rule.RuleType,
          sourceEntity: sourceEntity?.Title || "Unknown",
          sourceEntityId: rule.SourceEntityIdLookupId,
          sourceEntityType: sourceEntity?.EntityType,
          targetEntity: targetEntity?.Title || "Unknown",
          targetEntityId: rule.TargetEntityIdLookupId,
          targetEntityType: targetEntity?.EntityType,
          conditionMet: false,
          reason: "No signal available for source entity",
          impact: 0,
          impactUnit: rule.ImpactUnit,
        });
        return;
      }

      // Evaluate condition
      const conditionResult = this.evaluateCondition(
        rule.ConditionExpression,
        sourceSignal,
      );

      // Calculate impact
      const impact = conditionResult.met ? rule.ImpactValue || 0 : 0;

      evaluations.push({
        ruleId: rule.Id,
        ruleTitle: rule.Title,
        ruleType: rule.RuleType,
        sourceEntity: sourceEntity?.Title || "Unknown",
        sourceEntityId: rule.SourceEntityIdLookupId,
        sourceEntityType: sourceEntity?.EntityType,
        targetEntity: targetEntity?.Title || "Unknown",
        targetEntityId: rule.TargetEntityIdLookupId,
        targetEntityType: targetEntity?.EntityType,
        conditionMet: conditionResult.met,
        conditionExpression: rule.ConditionExpression,
        evaluationDetails: conditionResult.details,
        impact: impact,
        impactUnit: rule.ImpactUnit,
        priority: rule.Priority || 10,
      });
    });

    return evaluations;
  }

  /**
   * Evaluate a condition expression
   */
  /**
   * Translate a raw condition expression into a plain-English string.
   * e.g.  "NumericValue >= 0.5"  →  "Score must be at least 50% (current: 35%)"
   */
  describeCondition(expression, signal, met) {
    if (!expression) return null;
    const expr = expression.trim();

    // NumericValue comparisons
    const numMatch = expr.match(/NumericValue\s*(>=|<=|>|<|==|!=)\s*([0-9.]+)/i);
    if (numMatch) {
      const [, op, rawVal] = numMatch;
      const threshold = parseFloat(rawVal);
      const isScore = threshold >= 0 && threshold <= 1;
      const opLabel = { '>=': 'at least', '<=': 'at most', '>': 'above', '<': 'below', '==': 'exactly', '!=': 'not' };
      const fmtThreshold = isScore ? `${(threshold * 100).toFixed(0)}%` : threshold.toLocaleString();
      const label = isScore ? 'Score' : 'Value';
      const current = signal?.NumericValue ?? null;
      const fmtCurrent = current !== null
        ? isScore ? `${(current * 100).toFixed(0)}%` : Number(current).toLocaleString()
        : null;
      const metLabel = met ? '✓ met' : 'not met';
      return `${label} must be ${opLabel[op] || op} ${fmtThreshold}${fmtCurrent !== null ? ` (current: ${fmtCurrent})` : ''} — ${metLabel}`;
    }

    // Status comparisons
    const statusMatch = expr.match(/Status\s*(==|!=)\s*["']([^"']+)["']/i);
    if (statusMatch) {
      const [, op, val] = statusMatch;
      const opLabel = op === '==' ? 'must be' : 'must not be';
      const current = signal?.Status || null;
      const metLabel = met ? '✓ met' : 'not met';
      return `Status ${opLabel} "${val}"${current ? ` (current: "${current}")` : ''} — ${metLabel}`;
    }

    // BooleanValue comparisons
    const boolMatch = expr.match(/BooleanValue\s*(==|!=)\s*(true|false)/i);
    if (boolMatch) {
      const [, op, val] = boolMatch;
      const needsTrue = (op === '==' && val === 'true') || (op === '!=' && val === 'false');
      const metLabel = met ? '✓ met' : 'not met';
      return `${needsTrue ? 'Must be active/confirmed' : 'Must be inactive/not confirmed'} — ${metLabel}`;
    }

    // Fallback
    return met ? 'Condition met' : 'Condition not met';
  }

  evaluateCondition(expression, signal) {
    if (!expression || !signal) {
      return { met: false, details: "No signal data available" };
    }

    try {
      // Create evaluation context
      const context = {
        Status: signal.Status,
        NumericValue: signal.NumericValue,
        BooleanValue: signal.BooleanValue,
        TextValue: signal.TextValue,
      };

      // Parse and evaluate expression
      // Security note: In production, use a proper expression parser
      // This is a simplified version
      const result = this.safeEvaluate(expression, context);
      const met = Boolean(result);

      return {
        met,
        details: this.describeCondition(expression, signal, met),
        context: context,
      };
    } catch (error) {
      return {
        met: false,
        details: `Condition could not be evaluated`,
      };
    }
  }

  /**
   * Safe expression evaluation
   * In production, use a proper expression parser library
   */
  safeEvaluate(expression, context) {
    // Simple expression parser
    // Supports: ==, !=, >=, <=, >, <, &&, ||

    let expr = expression.trim();

    // Replace context variables
    Object.keys(context).forEach((key) => {
      const value = context[key];
      const valueStr = typeof value === "string" ? `'${value}'` : value;
      expr = expr.replace(new RegExp(key, "g"), valueStr);
    });

    // Handle null/undefined
    expr = expr.replace(/null|undefined/g, "null");

    try {
      // eslint-disable-next-line no-eval
      return eval(expr);
    } catch (error) {
      console.error("Expression evaluation error:", error);
      return false;
    }
  }

  /**
   * Identify dependency-based alerts
   * NEW METHOD - Generates alerts for failed dependencies
   */
  identifyDependencyAlerts(entities, ruleEvaluations, latestSignals) {
    const alerts = [];

    ruleEvaluations.forEach((evaluation) => {
      // Check for dependency failures that should generate alerts
      const shouldAlert =
        !evaluation.conditionMet &&
        evaluation.targetEntityType &&
        (evaluation.ruleType === "Required" ||
          evaluation.ruleType === "Blocks");

      if (shouldAlert) {
        const targetEntity = entities.find(
          (e) => e.Id === evaluation.targetEntityId,
        );
        const sourceEntity = entities.find(
          (e) => e.Id === evaluation.sourceEntityId,
        );

        if (!targetEntity || !sourceEntity) return;

        let severity = "Warning";
        let message = "";

        // Determine severity and message based on rule type and entity types
        if (evaluation.ruleType === "Required") {
          severity = "Critical";
          message = `${sourceEntity.Title} (${sourceEntity.EntityType}) is required for ${targetEntity.Title} (${targetEntity.EntityType}) but condition not met`;
        } else if (evaluation.ruleType === "Blocks") {
          severity = "Critical";
          message = `${sourceEntity.Title} (${sourceEntity.EntityType}) is blocking ${targetEntity.Title} (${targetEntity.EntityType})`;
        }

        // Special handling for CSF → Project dependencies
        if (
          sourceEntity.EntityType === "CSF" &&
          targetEntity.EntityType === "Project"
        ) {
          severity = "Critical";
          message = `CSF "${sourceEntity.Title}" not met - blocking Project "${targetEntity.Title}"`;
        }

        // Special handling for CSF → Metric dependencies
        if (
          sourceEntity.EntityType === "CSF" &&
          targetEntity.EntityType === "Metric"
        ) {
          severity = "Warning";
          message = `CSF "${sourceEntity.Title}" not met - affecting Metric "${targetEntity.Title}"`;
        }

        // Get the signal status for more context
        const sourceSignal = latestSignals.get(evaluation.sourceEntityId);
        const reasonNote = sourceSignal?.Reason ? `\nReason: ${sourceSignal.Reason}` : "";
        const conditionDesc = evaluation.evaluationDetails || this.describeCondition(evaluation.conditionExpression, sourceSignal, false) || '';
        const explanation = sourceSignal
          ? `Current status: ${sourceSignal.Status || "No signal data"}.${conditionDesc ? ` ${conditionDesc}` : ''}${reasonNote}`
          : (conditionDesc || 'Condition not met');

        alerts.push({
          entityId: evaluation.targetEntityId,
          entityTitle: targetEntity.Title,
          message: message,
          explanation: explanation,
          severity: severity,
          alertType: "Dependency",
          sourceEntity: sourceEntity.Title,
          sourceEntityType: sourceEntity.EntityType,
          targetEntityType: targetEntity.EntityType,
          ruleType: evaluation.ruleType,
          ruleTitle: evaluation.ruleTitle,
          impact: evaluation.impact,
          impactUnit: evaluation.impactUnit,
        });
      }
    });

    // Also check for unrealized contributions
    ruleEvaluations.forEach((evaluation) => {
      if (evaluation.ruleType === "Contributes" && !evaluation.conditionMet) {
        const targetEntity = entities.find(
          (e) => e.Id === evaluation.targetEntityId,
        );
        const sourceEntity = entities.find(
          (e) => e.Id === evaluation.sourceEntityId,
        );

        if (!targetEntity || !sourceEntity) return;

        const contribSignal = latestSignals.get(evaluation.sourceEntityId);
        const contribReasonNote = contribSignal?.Reason ? `\nReason: ${contribSignal.Reason}` : "";
        const impactDesc = evaluation.impact && evaluation.impact !== 0
          ? `Expected contribution of ${Number(evaluation.impact).toLocaleString()} ${evaluation.impactUnit === 'Absolute' ? 'units' : evaluation.impactUnit || 'units'} is not being achieved.`
          : `Expected contribution is not being achieved.`;
        const conditionDesc = evaluation.evaluationDetails || this.describeCondition(evaluation.conditionExpression, contribSignal, false) || '';
        alerts.push({
          entityId: evaluation.targetEntityId,
          entityTitle: targetEntity.Title,
          message: `${sourceEntity.Title} (${sourceEntity.EntityType}) contribution to ${targetEntity.Title} not realized`,
          explanation: `${impactDesc}${conditionDesc ? ` ${conditionDesc}` : ''}${contribReasonNote}`,
          severity: "Warning",
          alertType: "UnrealizedContribution",
          sourceEntity: sourceEntity.Title,
          sourceEntityType: sourceEntity.EntityType,
          targetEntityType: targetEntity.EntityType,
          ruleType: evaluation.ruleType,
          ruleTitle: evaluation.ruleTitle,
          impact: evaluation.impact,
          impactUnit: evaluation.impactUnit,
        });
      }
    });

    console.log(`[RuleEngine] Generated ${alerts.length} dependency alerts`);
    return alerts;
  }

  /**
   * Calculate target metrics for all entities
   */
  calculateTargetMetrics(entities, signals) {
    const metrics = [];

    entities.forEach((entity) => {
      if (entity.EntityType === "Metric" && entity.TargetValue) {
        metrics.push({
          entityId: entity.Id,
          entityTitle: entity.Title,
          targetValue: entity.TargetValue,
          targetUnit: entity.TargetUnit,
          currentSignal: signals.get(entity.Id),
        });
      }
    });

    return metrics;
  }

  /**
   * Calculate achievable production based on rules
   */
  /**
   * Calculate achievable production based on rules
   * NOW: Uses adjusted targets from met contributions
   */
  calculateAchievableMetrics(
    entities,
    signals,
    ruleEvaluations,
    adjustedTargetMetrics,
  ) {
    const achievable = new Map();

    // Initialize with ADJUSTED targets (not base targets)
    adjustedTargetMetrics.forEach((targetMetric) => {
      achievable.set(targetMetric.entityId, {
        entityId: targetMetric.entityId,
        entityTitle: targetMetric.entityTitle,
        baseTarget: targetMetric.baseTargetValue, // Original
        adjustedTarget: targetMetric.targetValue, // After contributions
        achievableValue: targetMetric.targetValue, // Start from adjusted target
        contributions: targetMetric.contributions || [],
        blockages: [],
        requirements: [],
      });
    });

    // Apply blocking/required rules (Contributes already applied to target)
    ruleEvaluations.forEach((evaluation) => {
      const targetMetric = Array.from(achievable.values()).find(
        (m) => m.entityTitle === evaluation.targetEntity,
      );

      if (!targetMetric) return;

      const targetEntity = entities.find((e) => e.Id === targetMetric.entityId);

      switch (evaluation.ruleType) {
        case "Required":
          targetMetric.requirements.push(evaluation);
          if (!evaluation.conditionMet) {
            // Required condition not met → block entire target
            targetMetric.achievableValue = 0;
            targetMetric.blockages.push({
              type: "Required",
              source: evaluation.sourceEntity,
              reason: "Required condition not met",
              impact: targetMetric.adjustedTarget,
            });
          }
          break;

        case "Blocks":
          if (evaluation.conditionMet) {
            // Blocking condition is true → reduce achievable
            const blockAmount =
              evaluation.impactUnit === "Percentage"
                ? targetMetric.achievableValue * (evaluation.impact / 100)
                : evaluation.impact;

            targetMetric.achievableValue -= blockAmount;
            targetMetric.blockages.push({
              type: "Blocks",
              source: evaluation.sourceEntity,
              reason: "Blocking condition met",
              impact: blockAmount,
            });
          }
          break;

        case "Contributes":
          // Contributions are now handled in target adjustment
          // Only track unrealized contributions here
          if (!evaluation.conditionMet) {
            const unrealizedAmount =
              evaluation.impactUnit === "Percentage"
                ? targetEntity.TargetValue * (evaluation.impact / 100)
                : evaluation.impact;

            targetMetric.achievableValue -= unrealizedAmount;
            targetMetric.blockages.push({
              type: "Unrealized Contribution",
              source: evaluation.sourceEntity,
              reason: "Contribution condition not met",
              impact: unrealizedAmount,
            });
          }
          break;
      }
    });

    // Ensure achievable doesn't go negative
    achievable.forEach((metric) => {
      metric.achievableValue = Math.max(0, metric.achievableValue);
    });

    return Array.from(achievable.values());
  }

  /**
   * Calculate blocked/unrealized upside
   * NOW: Compares against adjusted targets
   */
  calculateBlockedUpside(targets, achievable, ruleEvaluations) {
    const analysis = [];

    achievable.forEach((metric) => {
      const blocked = metric.adjustedTarget - metric.achievableValue;
      const percentBlocked =
        metric.adjustedTarget > 0 ? (blocked / metric.adjustedTarget) * 100 : 0;

      analysis.push({
        entityId: metric.entityId,
        entityTitle: metric.entityTitle,
        baseTarget: metric.baseTarget,
        adjustedTarget: metric.adjustedTarget,
        target: metric.adjustedTarget, // For backwards compatibility
        achievable: metric.achievableValue,
        blocked: blocked,
        percentBlocked: percentBlocked,
        blockages: metric.blockages,
        contributions: metric.contributions || [],
        rootCauses: this.identifyRootCauses(metric, ruleEvaluations),
      });
    });

    return analysis;
  }

  /**
   * Identify root causes of under-performance
   */
  identifyRootCauses(metric, ruleEvaluations) {
    const rootCauses = [];

    metric.blockages.forEach((blockage) => {
      rootCauses.push({
        source: blockage.source,
        type: blockage.type,
        impact: blockage.impact,
        reason: blockage.reason,
      });
    });

    // Also check unmet requirements
    metric.requirements.forEach((req) => {
      if (!req.conditionMet) {
        rootCauses.push({
          source: req.sourceEntity,
          type: "Unmet Requirement",
          impact: metric.adjustedTarget, // Use adjusted target
          reason: req.evaluationDetails,
        });
      }
    });

    return rootCauses;
  }

  /**
   * Build dependency graph for visualization
   */
  buildDependencyGraph(entities, rules, evaluations) {
    const nodes = entities.map((entity) => ({
      id: entity.Id,
      title: entity.Title,
      type: entity.EntityType,
      targetValue: entity.TargetValue,
      targetUnit: entity.TargetUnit,
    }));

    const edges = evaluations.map((evaluation) => ({
      source: evaluation.sourceEntity,
      target: evaluation.targetEntity,
      ruleType: evaluation.ruleType,
      conditionMet: evaluation.conditionMet,
      impact: evaluation.impact,
      impactUnit: evaluation.impactUnit,
    }));

    return { nodes, edges };
  }

  /**
   * Generate human-readable explanations
   * NOW: Shows base target vs adjusted target vs achievable
   */
  generateExplanations(
    entities,
    signals,
    ruleEvaluations,
    targets,
    achievable,
    blocked,
  ) {
    const explanations = {
      summary: "",
      details: [],
      recommendations: [],
    };

    // Overall summary with adjusted targets
    const totalBaseTarget = targets.reduce(
      (sum, m) => sum + m.baseTargetValue,
      0,
    );
    const totalAdjustedTarget = targets.reduce(
      (sum, m) => sum + m.targetValue,
      0,
    );
    const totalAchievable = achievable.reduce(
      (sum, m) => sum + m.achievableValue,
      0,
    );
    const totalBlocked = totalAdjustedTarget - totalAchievable;
    const totalUpside = totalAdjustedTarget - totalBaseTarget;
    const percentAchievable =
      totalAdjustedTarget > 0
        ? (totalAchievable / totalAdjustedTarget) * 100
        : 0;

    explanations.summary =
      totalUpside > 0
        ? `Base Target: ${totalBaseTarget.toFixed(0)}, Upside: +${totalUpside.toFixed(0)}, Adjusted Target: ${totalAdjustedTarget.toFixed(0)}, Achievable: ${totalAchievable.toFixed(0)} (${percentAchievable.toFixed(1)}%), Blocked: ${totalBlocked.toFixed(0)}`
        : `Target: ${totalAdjustedTarget.toFixed(0)}, Achievable: ${totalAchievable.toFixed(0)} (${percentAchievable.toFixed(1)}%), Blocked: ${totalBlocked.toFixed(0)}`;

    // Detailed explanations per metric
    blocked.forEach((analysis) => {
      const messages = [];

      // Show contributions if any
      if (analysis.contributions && analysis.contributions.length > 0) {
        const contributionsText = analysis.contributions
          .map((c) => `+${c.amount.toFixed(0)} from ${c.source}`)
          .join(", ");
        messages.push(`Upside realized: ${contributionsText}`);
      }

      if (analysis.blocked > 0) {
        const detail = {
          metric: analysis.entityTitle,
          message: `${analysis.entityTitle}: Base ${analysis.baseTarget.toFixed(0)} → Adjusted ${analysis.adjustedTarget.toFixed(0)} → Achievable ${analysis.achievable.toFixed(0)} (${analysis.blocked.toFixed(0)} blocked)`,
          rootCauses: analysis.rootCauses.map(
            (rc) =>
              `• ${rc.source}: ${rc.reason} (Impact: ${rc.impact.toFixed(0)})`,
          ),
        };

        explanations.details.push(detail);

        // Generate recommendations
        analysis.rootCauses.forEach((rc) => {
          explanations.recommendations.push({
            priority: rc.impact,
            action: `Address ${rc.source}: ${rc.type}`,
            potentialUpside: rc.impact,
          });
        });
      } else if (messages.length > 0) {
        // Show positive contributions even if nothing blocked
        explanations.details.push({
          metric: analysis.entityTitle,
          message: `${analysis.entityTitle}: ${messages.join("; ")}`,
          rootCauses: [],
        });
      }
    });

    // Sort recommendations by potential upside
    explanations.recommendations.sort(
      (a, b) => b.potentialUpside - a.potentialUpside,
    );

    return explanations;
  }

  /**
   * Calculate target metrics for all entities
   * NOW: Adjusts targets dynamically based on met contribution rules
   */
  calculateTargetMetrics(entities, signals) {
    const metrics = [];

    entities.forEach((entity) => {
      if (entity.EntityType === "Metric" && entity.TargetValue) {
        metrics.push({
          entityId: entity.Id,
          entityTitle: entity.Title,
          baseTargetValue: entity.TargetValue, // Original target
          targetValue: entity.TargetValue, // Will be adjusted
          targetUnit: entity.TargetUnit,
          currentSignal: signals.get(entity.Id),
        });
      }
    });

    return metrics;
  }

  /**
   * Adjust targets based on met contribution rules
   * NEW METHOD - Called after rule evaluation
   */
  adjustTargetsForMetContributions(targetMetrics, ruleEvaluations, entities) {
    const adjustedTargets = [...targetMetrics];

    ruleEvaluations.forEach((evaluation) => {
      // Only adjust for "Contributes" rules where condition IS MET
      if (evaluation.ruleType === "Contributes" && evaluation.conditionMet) {
        // Find the target metric for this rule
        const targetMetric = adjustedTargets.find(
          (m) => m.entityTitle === evaluation.targetEntity,
        );

        if (targetMetric) {
          const targetEntity = entities.find(
            (e) => e.Id === targetMetric.entityId,
          );

          // Calculate contribution amount
          const contributionAmount =
            evaluation.impactUnit === "Percentage"
              ? targetEntity.TargetValue * (evaluation.impact / 100)
              : evaluation.impact;

          // ADD to the target (upside realized)
          targetMetric.targetValue += contributionAmount;

          // Track the contribution
          if (!targetMetric.contributions) {
            targetMetric.contributions = [];
          }
          targetMetric.contributions.push({
            source: evaluation.sourceEntity,
            amount: contributionAmount,
            unit: evaluation.impactUnit,
            ruleTitle: evaluation.ruleTitle,
          });

          console.log(
            `[RuleEngine] Target adjusted: ${targetMetric.entityTitle} ` +
              `from ${targetMetric.baseTargetValue} to ${targetMetric.targetValue} ` +
              `(+${contributionAmount} from ${evaluation.sourceEntity})`,
          );
        }
      }
    });

    return adjustedTargets;
  }
}

export default new RuleEngine();
